import React, { useEffect, useState } from 'react';
import { DownOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Col, Row, Typography, theme } from 'antd';

const { Text } = Typography;

/**
 * "How to use" box at the top of a page: numbered steps in one or two columns,
 * each with an icon, a title and a line of text. Same look as the boxes on the
 * client reports page, but the page supplies the steps, so any page can use it.
 *
 * Opens by itself while `open` says the page has nothing yet, until the user
 * opens or closes it by hand.
 *
 * @param {Object} props
 * @param {string} props.title - Toggle label, e.g. "How to use this page"
 * @param {Array<{title: string, steps: Array<{icon?: Function, title: string, text?: string, onClick?: Function}>}>} props.sections
 * @param {string} [props.note] - Closing line under the steps
 * @param {boolean} [props.open] - Whether it starts open; undefined while loading
 * @returns {JSX.Element}
 */
export default function HowToCard({ title, sections, note, open }) {
  const { token } = theme.useToken();
  const [expanded, setExpanded] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!touched && open !== undefined) setExpanded(open);
  }, [open, touched]);

  const toggle = () => {
    setTouched(true);
    setExpanded((e) => !e);
  };

  const span = sections.length > 1 ? 12 : 24;
  // Steps are numbered straight through all columns: each section restarting
  // at 1 put two or three steps with the same number side by side.
  const firstNumber = sections.map((_, idx) =>
    sections.slice(0, idx).reduce((sum, s) => sum + s.steps.length, 1)
  );

  return (
    <div
      style={{
        border: `1px solid ${token.colorPrimaryBorder}`,
        borderLeft: `4px solid ${token.colorPrimary}`,
        borderRadius: token.borderRadiusLG,
        background: token.colorPrimaryBg,
        padding: expanded ? '12px 16px 4px' : '8px 16px',
        marginBottom: 16,
      }}
    >
      <Button
        type="text"
        shape="round"
        onClick={toggle}
        aria-expanded={expanded}
        style={{
          padding: 0,
          height: 'auto',
          fontWeight: 600,
          color: token.colorPrimary,
        }}
        icon={<QuestionCircleOutlined />}
      >
        {title}
        <DownOutlined
          style={{
            fontSize: 11,
            marginInlineStart: 6,
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        />
      </Button>
      {expanded && (
        <div style={{ marginTop: 12 }}>
          <Row gutter={[32, 0]}>
            {sections.map((section, sectionIdx) => (
              <Col key={section.title} xs={24} lg={span}>
                <Text
                  strong
                  style={{
                    display: 'block',
                    marginBottom: 10,
                    color: token.colorPrimary,
                  }}
                >
                  {section.title}
                </Text>
                {section.steps.map((step, i) => {
                  const Icon = step.icon || QuestionCircleOutlined;
                  return (
                    <div
                      key={step.title}
                      style={{ display: 'flex', gap: 12, marginBottom: 12 }}
                    >
                      <div
                        style={{
                          flex: '0 0 40px',
                          height: 40,
                          borderRadius: 10,
                          background: token.colorBgContainer,
                          border: `1px solid ${token.colorPrimaryBorder}`,
                          color: token.colorPrimary,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 18,
                          position: 'relative',
                        }}
                      >
                        <Icon />
                        <span
                          style={{
                            position: 'absolute',
                            top: -7,
                            left: -7,
                            width: 18,
                            height: 18,
                            borderRadius: 9,
                            background: token.colorPrimary,
                            color: token.colorWhite,
                            fontSize: 11,
                            fontWeight: 600,
                            lineHeight: '18px',
                            textAlign: 'center',
                          }}
                        >
                          {firstNumber[sectionIdx] + i}
                        </span>
                      </div>
                      <div style={{ minWidth: 0 }}>
                        {step.onClick ? (
                          <Typography.Link strong onClick={step.onClick}>
                            {step.title}
                          </Typography.Link>
                        ) : (
                          <Text strong>{step.title}</Text>
                        )}
                        {step.text && (
                          <div
                            style={{
                              fontSize: 13,
                              color: token.colorTextSecondary,
                            }}
                          >
                            {step.text}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </Col>
            ))}
          </Row>
          {note && (
            <div
              style={{
                fontSize: 12,
                color: token.colorTextSecondary,
                marginBottom: 8,
              }}
            >
              {note}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
